import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CloseOutlined, MoreOutlined } from '@ant-design/icons';
import s from './s.module.less';
import { Button, Drawer, Dropdown, message } from 'antd';
import type { MenuProps } from 'antd';
import IntakeFormContent from './component/IntakeFormContent';
import { EBusinessType, TextVisitCase } from 'types/textVisit';
import { getRefillServiceTitle } from 'pages/dashboard/TextVisitPage/utils';
import { IVideoTreatmentDetail } from 'types/treatment';
import SendFaxDrawer from 'pages/dashboard/EFaxPage/EFaxDashboardPage/SendFaxDrawer';
import type { SendFaxSendContext } from 'pages/dashboard/EFaxPage/EFaxDashboardPage/SendFaxDrawer';
import useFaxNumber from 'hooks/useFaxNumber';
import { faxContactList } from 'api/fax';
import type { FaxContact } from 'types/eFax';
import {
    extractFaxContactListPage,
    isFaxScheduleSuccess,
    mapFaxContactVoToFaxContact,
} from 'utils/eFaxScheduleRows';
import { isEfaxSubscriptionCanceledStatus } from 'utils/eFaxSubscriptionStatus';

type Props = {
    show?: boolean;
    onClose?: () => void;
    textVisitDetail?: TextVisitCase;
    appointmentTreatmentDetail?: IVideoTreatmentDetail;
};

const IntakeFormDrawer = (props: Props) => {
    const { show, onClose, textVisitDetail, appointmentTreatmentDetail } = props;
    const speciality =
        textVisitDetail?.specialty || textVisitDetail?.conditions || appointmentTreatmentDetail?.serviceSpecialty;
    const serviceTypeName = textVisitDetail?.serviceType || appointmentTreatmentDetail?.serviceType;
    const isTextVisit = !!textVisitDetail;

    const [sendFaxOpen, setSendFaxOpen] = useState(false);
    const [faxSendContext, setFaxSendContext] = useState<SendFaxSendContext | null>(null);
    const [faxContacts, setFaxContacts] = useState<FaxContact[]>([]);
    const [faxNumber] = useFaxNumber();
    const faxInactive =
        !faxNumber?.faxNumber || isEfaxSubscriptionCanceledStatus(faxNumber.status);

    const faxIntakeContext = useMemo((): SendFaxSendContext | null => {
        if (textVisitDetail?.id != null && textVisitDetail.clientId != null) {
            return {
                source: 'INTAKE',
                sourceId: textVisitDetail.id,
                intakeBusinessType: EBusinessType.TEXT_VISIT,
                clientId: textVisitDetail.clientId,
            };
        }
        if (appointmentTreatmentDetail?.id != null && appointmentTreatmentDetail.clientId != null) {
            return {
                source: 'INTAKE',
                sourceId: appointmentTreatmentDetail.id,
                intakeBusinessType: EBusinessType.APPOINTMENT,
                clientId: appointmentTreatmentDetail.clientId,
            };
        }
        return null;
    }, [textVisitDetail, appointmentTreatmentDetail]);

    const fetchFaxContacts = useCallback(async () => {
        const result = await faxContactList({ current: 1, size: 200 });
        const body = result?.data;
        if (!result?.error && body && isFaxScheduleSuccess(body)) {
            const { records } = extractFaxContactListPage(body);
            setFaxContacts(records.map(mapFaxContactVoToFaxContact));
        }
    }, []);

    useEffect(() => {
        if (!sendFaxOpen) return;
        void fetchFaxContacts();
    }, [sendFaxOpen, fetchFaxContacts]);

    const openSendFax = () => {
        if (!faxIntakeContext) {
            message.warning('Intake details are still loading. Try again in a moment.');
            return;
        }
        if (faxInactive) {
            message.warning('Activate eFax from the sidebar before sending faxes.');
            return;
        }
        setFaxSendContext(faxIntakeContext);
        setSendFaxOpen(true);
    };

    const closeSendFax = () => {
        setSendFaxOpen(false);
        setFaxSendContext(null);
    };

    const overflowMenu: MenuProps = {
        items: [{ key: 'send-fax', label: 'Send fax' }],
        onClick: ({ key, domEvent }) => {
            domEvent?.stopPropagation?.();
            if (key === 'send-fax') openSendFax();
        },
    };

    return (
        <>
            <Drawer
                open={show}
                width="1100"
                onClose={onClose}
                placement="right"
                rootClassName={s.drawer}
                title={
                    <div className={s.header}>
                        <div className={s.headerTitle}>
                            {isTextVisit && (
                                <span className="text-base font-medium">
                                    {(() => {
                                        const refillTitle = getRefillServiceTitle({
                                            serviceType: textVisitDetail?.serviceType,
                                            specialty: textVisitDetail?.specialty ?? null,
                                            assessmentName: textVisitDetail?.assessmentName ?? null,
                                        });
                                        return <>Text visit: {refillTitle || textVisitDetail?.assessmentName}</>;
                                    })()}
                                </span>
                            )}
                            {!isTextVisit && (
                                <span className="text-base font-medium">
                                    Intake forms for {speciality}: {serviceTypeName}
                                </span>
                            )}
                        </div>
                        <div className={s.headerActions}>
                            <Dropdown menu={overflowMenu} trigger={['click']} placement="bottomRight">
                                <Button
                                    type="text"
                                    icon={<MoreOutlined style={{ fontSize: 18 }} />}
                                    aria-label="More actions"
                                />
                            </Dropdown>
                            <Button type="text" icon={<CloseOutlined />} onClick={onClose} className={s.closeBtn} />
                        </div>
                    </div>
                }
                closeIcon={null}
                footer={null}
                destroyOnClose
            >
                <IntakeFormContent
                    textVisitDetail={textVisitDetail}
                    appointmentTreatmentDetail={appointmentTreatmentDetail}
                />
            </Drawer>
            <SendFaxDrawer
                open={sendFaxOpen}
                onClose={closeSendFax}
                contacts={faxContacts}
                faxSendContext={faxSendContext}
                onContactsChanged={() => void fetchFaxContacts()}
            />
        </>
    );
};

export default IntakeFormDrawer;
